import { useCallback, useEffect, useState } from "react";
import { Card, BlockStack, InlineStack, Text, Button, Toast } from "@shopify/polaris";
import supabase from "../../supabase-client";
import { formatPrice } from "../../utility/funcs";
import { MIN_LISTED_PAYMENT_CENTS } from "../../utility/payments";
import ApplyPaymentModal from "./ApplyPaymentModal";

// Money this reservation's client has sent that is not applied to anything yet,
// shown inside the reservation so it can be applied here in one or two clicks.
//
// Renders NOTHING when there is none, so it costs no space on the page. Looks
// at both possible payers: the company (for a company reservation) and the
// driver/customer — apply_payment accepts either when they own the reservation,
// except a driver's card payment on a company reservation.
//
// This is also the answer to "stop hand-typing payments that already arrived":
// the money is on screen before anyone reaches for Agregar Pago.

const RAIL_LABEL = { spei: "SPEI", card: "Tarjeta", cash: "Efectivo" };
const money = (c) => formatPrice(c / 100);

function UnappliedPaymentsCard({ reservationId, customerId, customerName, companyId, companyName, onApplied }) {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    const payers = [];
    if (companyId) payers.push(`and(payer_type.eq.company,payer_id.eq.${Number(companyId)})`);
    if (customerId) payers.push(`and(payer_type.eq.customer,payer_id.eq.${Number(customerId)})`);
    if (!payers.length) return setRows([]);

    const { data: pays } = await supabase
      .from("payments")
      .select("*")
      .eq("direction", "in")
      .eq("mode", "live")
      .gte("amount_cents", MIN_LISTED_PAYMENT_CENTS)
      .or(payers.join(","))
      .order("occurred_at", { ascending: true });
    const ids = (pays || []).map((p) => p.id);
    if (!ids.length) return setRows([]);

    const { data: bals } = await supabase
      .from("payment_balances")
      .select("payment_id, unapplied_cents")
      .in("payment_id", ids);
    const bal = Object.fromEntries((bals || []).map((b) => [b.payment_id, Number(b.unapplied_cents)]));
    setRows(
      (pays || [])
        .map((p) => ({ ...p, unapplied_cents: bal[p.id] ?? p.amount_cents }))
        .filter((p) => p.unapplied_cents >= MIN_LISTED_PAYMENT_CENTS)
        // A card in the driver's name cannot go to a company reservation
        // (apply_payment refuses it): the stamp was a human pick, not proof.
        .filter((p) => !(companyId && p.payer_type === "customer" && p.rail === "card"))
    );
  }, [customerId, companyId]);

  useEffect(() => {
    load();
    const channel = supabase
      .channel(`unapplied-${reservationId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "payments" }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "payment_applications" }, () => load())
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [load, reservationId]);

  // Keep an open modal on fresh numbers after an apply/reverse.
  useEffect(() => {
    if (!selected) return;
    const fresh = rows.find((r) => r.id === selected.id);
    if (fresh && fresh.unapplied_cents !== selected.unapplied_cents) setSelected(fresh);
  }, [rows, selected]);

  if (!rows.length) return null;

  const nameOf = (p) => (p.payer_type === "company" ? companyName : customerName);

  return (
    <>
      <Card>
        <BlockStack gap="400">
          <Text variant="headingMd" as="h2">Pagos sin aplicar</Text>
          {rows.map((p) => (
            <InlineStack key={p.id} align="space-between" blockAlign="center" gap="400" wrap={false}>
              <BlockStack gap="100">
                <Text as="span" variant="headingSm" numeric>{money(p.unapplied_cents)}</Text>
                <Text as="span" tone="subdued" variant="bodySm">
                  {RAIL_LABEL[p.rail] || p.rail} ·{" "}
                  {new Date(p.occurred_at).toLocaleString("es-MX", {
                    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                  })}
                </Text>
              </BlockStack>
              <Button variant="primary" size="large" onClick={() => setSelected(p)}>Aplicar</Button>
            </InlineStack>
          ))}
        </BlockStack>
      </Card>

      {selected && (
        <ApplyPaymentModal
          open
          payment={selected}
          payerName={nameOf(selected)}
          unappliedCents={selected.unapplied_cents}
          reservationId={reservationId}
          onClose={() => setSelected(null)}
          onApplied={(opts) => {
            if (opts?.message) setToast({ content: opts.message, error: opts.tone === "warning" });
            if (!opts?.keepOpen) setSelected(null);
            onApplied?.();
          }}
        />
      )}

      {toast && (
        <Toast
          content={toast.content}
          error={toast.error}
          duration={toast.error ? 12000 : 5000}
          onDismiss={() => setToast(null)}
        />
      )}
    </>
  );
}

export default UnappliedPaymentsCard;
